import { useMemo } from "react";
import { addDays } from "date-fns";
import useCalendarStore from "@/store/calendar-store";
import {
  dateToLocalTime,
  formatDateWithLocale,
  formatInTz,
} from "@/utils/format-date-with-locale";
import { capitalize } from "@/utils/capitalize";
import { useLocale, useTranslations } from "../../../../../i18n";
import { AgendaEvent } from "./agenda-event";

type Props = {
  events: any[];
  isLoading: boolean;
  onEventClick: (event: any) => void;
  onWaitlistJoin?: (event: any) => void;
  timezone?: string;
};

const DEFAULT_TZ = "Europe/Zurich";

const getEventStart = (event: any) => event.startAt ?? event.startTime;

function isSameDayInTz(a: Date, b: Date, tz: string) {
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
  return formatInTz(a, "yyyy-MM-dd", tz) === formatInTz(b, "yyyy-MM-dd", tz);
}

const WeeklyAgendaView = ({
  events,
  isLoading,
  onEventClick,
  onWaitlistJoin,
  timezone = DEFAULT_TZ,
}: Props) => {
  const { startOfTheWeek } = useCalendarStore();
  const locale = useLocale();
  const t = useTranslations("calendar");

  const days = useMemo(() => {
    const weekDays = [...Array(7)].map((_, i) => addDays(startOfTheWeek, i));

    return weekDays.map((day) => {
      const dayEvents = (events ?? [])
        .filter((e) => {
          const eventStart = getEventStart(e);
          if (!eventStart) return false;
          const eventDate = dateToLocalTime(new Date(eventStart), timezone);
          return isSameDayInTz(eventDate, day, timezone);
        })
        .sort((a, b) => {
          const aStart = getEventStart(a);
          const bStart = getEventStart(b);
          return +new Date(aStart ?? 0) - +new Date(bStart ?? 0);
        });

      return { day, dayEvents };
    });
  }, [events, startOfTheWeek, timezone]);

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="bg-white rounded-2xl p-6 shadow-sm border">
        <div className="flex flex-col gap-8">
          {days.map(({ day, dayEvents }) => (
            <div
              key={formatInTz(day, "yyyy-MM-dd", timezone)}
              className="flex flex-col gap-3"
            >
              {/* Day header */}
              <div className="flex flex-col gap-1">
                <div className="text-xl font-semibold text-gray-900">
                  {capitalize(
                    formatDateWithLocale(day, "EEEE d MMMM", locale, timezone)
                  )}
                </div>
              </div>

              {/* Content */}
              {isLoading ? (
                <div className="h-10 w-40 bg-gray-100 rounded-md" />
              ) : dayEvents.length === 0 ? (
                <div className="text-gray-600">{t("noClasses")}</div>
              ) : (
                <div className="flex flex-col gap-4">
                  {dayEvents.map((event) => (
                    <AgendaEvent
                      key={event.id}
                      event={event}
                      onClick={() => onEventClick(event)}
                      onWaitlistJoin={onWaitlistJoin}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeeklyAgendaView;
